import React, { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FmTextField, FmIcon, FmButton } from "../../../../../Components";
import { ValidationUtils } from "../../../../../Utils";
import { useTheme } from "../../../../../Hooks";

import useStyles from "./UpdatePasswordForm.styles";

interface UpdatePasswordProps {
  onSubmit: (password: string) => void;
  loading: boolean;
  error?: boolean;
}

interface IFormInputs {
  password: string;
  confirmPassword: string;
}

const UpdatePasswordForm = ({ onSubmit, loading, error }: UpdatePasswordProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();

  const [passwordIsVisible, setPasswordIsVisible] = useState<boolean>(false);
  const [confirmPasswordIsVisible, setConfirmPasswordIsVisible] = useState<boolean>(false);

  const {
    handleSubmit,
    getValues,
    control,
    register,
    watch,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: "onSubmit",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const handleFormSubmit: SubmitHandler<IFormInputs> = (data) => {
    onSubmit(data.password);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={classes.container}>
      <Controller
        name={"password"}
        control={control}
        rules={{
          required: {
            value: true,
            message: t("validationErrors.fieldIsRequired"),
          },
          minLength: {
            value: 8,
            message: t("validationErrors.passwordValidation"),
          },
          pattern: {
            value: ValidationUtils.StringRegex.PASSWORD,
            message: t("validationErrors.passwordValidation"),
          },
        }}
        render={({ field }) => {
          return (
            <FmTextField
              {...register("password")}
              {...field}
              label={t("signUp.password")}
              variant="filled"
              type={passwordIsVisible ? "text" : "password"}
              error={errors.password != undefined}
              value={field.value}
              onIconClick={() => setPasswordIsVisible((visible: boolean) => !visible)}
              inputProps={{
                icon: <FmIcon url={theme.images.eye} title={"visible"} />,
              }}
              helperText={errors.password?.message || ""}
            />
          );
        }}
      />
      <div className={classes.input}>
        <Controller
          name={"confirmPassword"}
          control={control}
          rules={{
            required: {
              value: true,
              message: t("validationErrors.fieldIsRequired"),
            },
            validate: ((value: string) => (value !== watch("password") ? t("validationErrors.passwordsDontMatch") : undefined)) as any,
          }}
          render={({ field }) => {
            return (
              <FmTextField
                {...register("confirmPassword")}
                {...field}
                label={t("signUp.confirmPassword")}
                variant="filled"
                type={confirmPasswordIsVisible ? "text" : "password"}
                error={errors.confirmPassword != undefined}
                value={field.value}
                displayIcon={true}
                onIconClick={() => setConfirmPasswordIsVisible((visible: boolean) => !visible)}
                inputProps={{
                  icon: <FmIcon url={theme.images.eye} title={"visible"} />,
                }}
                helperText={errors.confirmPassword?.message || ""}
              />
            );
          }}
        />
      </div>
      <div className={classes.buttonContainer}>
        <FmButton
          fullWidth="regular"
          type="submit"
          // onClick={onSubmit}
          loading={loading}
          disabled={loading}
        >
          {t("updatePassword.submitMessage")}
        </FmButton>
      </div>
    </form>
  );
};

export default UpdatePasswordForm;
